import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, RefreshCw, Search, Inbox, Trash2, Store } from "lucide-react";
import { API, LOGO } from "@/lib/site";

const SUPPORT_STATUS = ["open", "pending", "resolved"];
const DEL_STATUS = ["pending_verification", "verified", "processing", "completed", "failed"];

export default function AdminDashboard() {
  const nav = useNavigate();
  const token = typeof window !== "undefined" ? localStorage.getItem("orrbbit_admin_token") : null;
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [tab, setTab] = useState("support");
  const [support, setSupport] = useState([]);
  const [deletions, setDeletions] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const logout = useCallback(() => { localStorage.removeItem("orrbbit_admin_token"); nav("/admin/login"); }, [nav]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (status) params.status = status;
      const [s, d, b] = await Promise.all([
        axios.get(`${API}/admin/support-requests`, { ...auth, params }),
        axios.get(`${API}/admin/deletion-requests`, { ...auth, params }),
        axios.get(`${API}/admin/businesses`, auth),
      ]);
      setSupport(s.data); setDeletions(d.data); setBusinesses(b.data);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) { logout(); }
      else toast.error("Failed to load data");
    } finally { setLoading(false); }
  }, [q, status]); // eslint-disable-line

  useEffect(() => {
    if (!token) { nav("/admin/login"); return; }
    axios.get(`${API}/auth/me`, auth).then(load).catch(() => logout());
  }, []); // eslint-disable-line

  const updateSupport = async (id, changes) => {
    try { const res = await axios.patch(`${API}/admin/support-requests/${id}`, changes, auth);
      setSupport((prev) => prev.map((x) => (x.id === id ? res.data : x))); toast.success("Updated");
    } catch { toast.error("Update failed"); }
  };
  const updateDeletion = async (id, changes) => {
    try { const res = await axios.patch(`${API}/admin/deletion-requests/${id}`, changes, auth);
      setDeletions((prev) => prev.map((x) => (x.id === id ? res.data : x))); toast.success("Updated");
    } catch { toast.error("Update failed"); }
  };
  const updateBusiness = async (id, verification_status) => {
    try { const res = await axios.patch(`${API}/admin/businesses/${id}`, { verification_status }, auth);
      setBusinesses((prev) => prev.map((x) => (x.id === id ? { ...x, ...res.data } : x))); toast.success(`Business ${verification_status}`);
    } catch { toast.error("Update failed"); }
  };

  const sel = "rounded-lg border border-input bg-white px-2 py-1 text-sm text-navy";

  return (
    <div data-testid="admin-dashboard">
      <header className="sticky top-0 z-10 border-b border-border bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3">
          <div className="flex items-center gap-3"><img src={LOGO} alt="Orrbbit" className="h-7 w-auto" /><span className="rounded-full bg-teal/10 px-2 py-0.5 text-xs font-semibold text-teal">Admin</span></div>
          <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-medium text-navy hover:bg-lightbg"><LogOut size={15} /> Sign out</button>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-5 py-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex gap-2">
            <button onClick={() => setTab("support")} data-testid="tab-support" className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium ${tab === "support" ? "bg-navy text-white" : "bg-white text-navy ring-1 ring-border"}`}><Inbox size={15} /> Support ({support.length})</button>
            <button onClick={() => setTab("deletion")} data-testid="tab-deletion" className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium ${tab === "deletion" ? "bg-navy text-white" : "bg-white text-navy ring-1 ring-border"}`}><Trash2 size={15} /> Deletions ({deletions.length})</button>
            <button onClick={() => setTab("businesses")} data-testid="tab-businesses" className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium ${tab === "businesses" ? "bg-navy text-white" : "bg-white text-navy ring-1 ring-border"}`}><Store size={15} /> Businesses ({businesses.length})</button>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-bodygrey" />
              <input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search reference/email…" className="rounded-full border border-input bg-white py-2 pl-9 pr-4 text-sm text-navy" />
            </div>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className={sel}>
              <option value="">All statuses</option>
              {(tab === "support" ? SUPPORT_STATUS : DEL_STATUS).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <button onClick={load} className="inline-flex items-center gap-1 rounded-full bg-teal px-4 py-2 text-sm font-semibold text-white"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Apply</button>
          </div>
        </div>

        {tab === "businesses" ? (
          <div className="mt-6 space-y-4" data-testid="businesses-list">
            {businesses.length === 0 && <p className="text-bodygrey">No Business accounts yet.</p>}
            {businesses.map((b) => (
              <div key={b.id} className="rounded-2xl border border-border bg-white p-5 shadow-soft" data-testid={`business-row-${b.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div><span className="font-semibold text-navy">{b.business_name}</span><span className="ml-3 rounded-full bg-lightbg px-2 py-0.5 text-xs text-navy">{b.category || "Business"}</span></div>
                  <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${b.verification_status === "verified" ? "bg-teal/10 text-teal" : "bg-orange/10 text-orange"}`} data-testid={`business-status-${b.id}`}>{b.verification_status}</span>
                </div>
                <p className="mt-1 text-sm text-bodygrey">{b.email} · {b.address}{b.country ? `, ${b.country}` : ""} · {b.event_count ?? 0} events · joined {new Date(b.created_at).toLocaleDateString()}</p>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  {b.verification_status !== "verified" && <button onClick={() => updateBusiness(b.id, "verified")} data-testid={`business-verify-${b.id}`} className="rounded-lg bg-teal px-3 py-1.5 text-sm font-semibold text-white">Approve / Verify</button>}
                  {b.verification_status !== "more_info_required" && <button onClick={() => updateBusiness(b.id, "more_info_required")} className="rounded-lg bg-lightbg px-3 py-1.5 text-sm text-navy hover:bg-orange/10">Request more info</button>}
                  {b.verification_status !== "suspended" && <button onClick={() => updateBusiness(b.id, "suspended")} data-testid={`business-suspend-${b.id}`} className="rounded-lg bg-lightbg px-3 py-1.5 text-sm text-red-500 hover:bg-red-50">Suspend</button>}
                </div>
              </div>
            ))}
          </div>
        ) : tab === "support" ? (
          <div className="mt-6 space-y-4" data-testid="support-list">
            {support.length === 0 && <p className="text-bodygrey">No support requests.</p>}
            {support.map((r) => (
              <div key={r.id} className="rounded-2xl border border-border bg-white p-5 shadow-soft">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div><span className="font-mono text-sm font-semibold text-teal">{r.reference}</span><span className="ml-3 rounded-full bg-lightbg px-2 py-0.5 text-xs text-navy">{r.category}</span></div>
                  <span className="text-xs text-bodygrey">{new Date(r.created_at).toLocaleString()}</span>
                </div>
                <h3 className="mt-2 font-semibold text-navy">{r.subject}</h3>
                <p className="mt-1 text-sm text-bodygrey">From {r.name} ({r.email}){r.account_email ? ` · account: ${r.account_email}` : ""}</p>
                <p className="mt-2 whitespace-pre-line text-sm text-navy">{r.description}</p>
                {r.notes?.length > 0 && (
                  <div className="mt-3 space-y-1 rounded-lg bg-lightbg p-3 text-xs text-bodygrey">
                    {r.notes.map((n, i) => <div key={i}><strong className="text-navy">{n.by}</strong>: {n.text}</div>)}
                  </div>
                )}
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <label className="text-xs text-bodygrey">Status</label>
                  <select className={sel} value={r.status} onChange={(e) => updateSupport(r.id, { status: e.target.value })} data-testid={`support-status-${r.reference}`}>
                    {SUPPORT_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <input placeholder="Assign to…" defaultValue={r.assignee || ""} onBlur={(e) => e.target.value !== (r.assignee || "") && updateSupport(r.id, { assignee: e.target.value })} className={sel} />
                  <input placeholder="Add internal note + Enter" className={`${sel} flex-1 min-w-[180px]`} onKeyDown={(e) => { if (e.key === "Enter" && e.target.value.trim()) { updateSupport(r.id, { note: e.target.value.trim() }); e.target.value = ""; } }} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-6 space-y-4" data-testid="deletion-list">
            {deletions.length === 0 && <p className="text-bodygrey">No deletion requests.</p>}
            {deletions.map((r) => (
              <div key={r.id} className="rounded-2xl border border-border bg-white p-5 shadow-soft">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-mono text-sm font-semibold text-teal">{r.reference}</span>
                  <span className="text-xs text-bodygrey">{new Date(r.created_at).toLocaleString()}</span>
                </div>
                <p className="mt-2 text-sm text-navy">Email: <strong>{r.email}</strong></p>
                <p className="text-sm text-bodygrey">Verified: {r.verified ? "Yes" : "No"}{r.verified_at ? ` (${new Date(r.verified_at).toLocaleString()})` : ""}</p>
                {r.failure_reason && <p className="text-sm text-red-500">Failure: {r.failure_reason}</p>}
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <label className="text-xs text-bodygrey">Status</label>
                  <select className={sel} value={r.status} onChange={(e) => updateDeletion(r.id, { status: e.target.value })} data-testid={`deletion-status-${r.reference}`}>
                    {DEL_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <button onClick={() => updateDeletion(r.id, { status: "processing" })} className="rounded-lg bg-lightbg px-3 py-1 text-sm text-navy hover:bg-teal/10">Retry processing</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

